import { ChartOptions, Chart as ChartJS } from 'chart.js';
import {
  CurrentPriceBand,
  CurrentPricePerLabeler,
  PricingGranularity,
  PriceElasticityData,
  PricingTrendItem,
} from 'Interfaces/ProductWikiInterfaces/pricing.types';
import { AIInsightItem } from 'Interfaces/ProductWikiInterfaces/aiInsights.types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ProductAnalysisStore } from 'stores/ProductWikiStores/ProductAnalysisStore';
import { productWikiServices } from 'services/productWikiServices';

// Helper interface for react-chartjs-2 chart ref that can be accessed in different ways
interface ChartRefWrapper {
  chartInstance?: ChartJS;
  chart?: ChartJS;
  getChart?: () => ChartJS;
  isZoomedOrPanned?: () => boolean;
  resetZoom?: () => void;
}

const usePriceTab = (_productAnalysisStore: ProductAnalysisStore) => {
  const [searchParams] = useSearchParams();
  const [priceView, setPriceView] = useState<'chart' | 'table'>('chart');
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const priceBoxPlotRef = useRef<HTMLCanvasElement>(null);

  // Trend granularity state (monthly or quarterly) - toggles between pre-fetched data
  const [trendGranularity, setTrendGranularity] = useState<PricingGranularity>('monthly');

  // ===== CURRENT PRICE BAND STATE (Separate loader) =====
  const [currentBandData, setCurrentBandData] = useState<CurrentPriceBand | null>(null);
  const [isCurrentBandLoading, setIsCurrentBandLoading] = useState<boolean>(true);
  const [currentBandError, setCurrentBandError] = useState<string | null>(null);
  const currentBandAbortControllerRef = useRef<AbortController | null>(null);

  // ===== HISTORICAL TREND STATE (Separate loader) =====
  const [trendMonthlyData, setTrendMonthlyData] = useState<PricingTrendItem[]>([]);
  const [trendQuarterlyData, setTrendQuarterlyData] = useState<PricingTrendItem[]>([]);
  const [isHistoricalTrendLoading, setIsHistoricalTrendLoading] = useState<boolean>(true);
  const [historicalTrendError, setHistoricalTrendError] = useState<string | null>(null);
  const historicalTrendAbortControllerRef = useRef<AbortController | null>(null);

  // ===== LABELER PRICING STATE (Separate loader) =====
  const [labelerPricingData, setLabelerPricingData] = useState<CurrentPricePerLabeler[]>([]);
  const [isLabelerPricingLoading, setIsLabelerPricingLoading] = useState<boolean>(true);
  const [labelerPricingError, setLabelerPricingError] = useState<string | null>(null);
  const labelerPricingAbortControllerRef = useRef<AbortController | null>(null);

  // AI Insights state
  const [aiInsights, setAiInsights] = useState<AIInsightItem[] | null>(null);
  const [isInsightsLoading, setIsInsightsLoading] = useState<boolean>(true);
  const [insightsError, setInsightsError] = useState<string | null>(null);
  const insightsAbortControllerRef = useRef<AbortController | null>(null);

  // Price Elasticity state - separate loader for price-volume relationship data
  const [priceElasticityData, setPriceElasticityData] = useState<PriceElasticityData | null>(null);
  const [isPriceElasticityLoading, setIsPriceElasticityLoading] = useState<boolean>(true);
  const [priceElasticityError, setPriceElasticityError] = useState<string | null>(null);
  const priceElasticityAbortControllerRef = useRef<AbortController | null>(null);
  
  // Zoom state for Price Elasticity chart
  const [isPriceElasticityZoomed, setIsPriceElasticityZoomed] = useState<boolean>(false);
  const priceElasticityChartRef = useRef<ChartJS | null>(null);

  // Zoom state for Historical Pricing Trend chart
  const [isHistoricalTrendZoomed, setIsHistoricalTrendZoomed] = useState<boolean>(false);
  const historicalTrendChartRef = useRef<ChartJS | null>(null);

  // Zoom state for Historical Pricing Trend modal chart
  const [isHistoricalTrendModalZoomed, setIsHistoricalTrendModalZoomed] = useState<boolean>(false);
  const historicalTrendModalChartRef = useRef<ChartJS | null>(null);

  // Get GFC from URL params
  const gfc = searchParams.get('gfc');

  // Get the current trend data based on selected granularity (no refetch needed!)
  const currentTrendData = useMemo(() => {
    return trendGranularity === 'monthly' ? trendMonthlyData || [] : trendQuarterlyData || [];
  }, [trendMonthlyData, trendQuarterlyData, trendGranularity]);

  // ===== FETCH CURRENT PRICE BAND =====
  const fetchCurrentBandData = useCallback(async () => {
    if (!gfc || gfc.trim() === '') {
      setCurrentBandData(null);
      setIsCurrentBandLoading(false);
      return;
    }

    // Abort previous request if it exists
    if (currentBandAbortControllerRef.current) {
      currentBandAbortControllerRef.current.abort();
      currentBandAbortControllerRef.current = null;
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    currentBandAbortControllerRef.current = abortController;

    setIsCurrentBandLoading(true);
    setCurrentBandError(null);

    try {
      const response = await productWikiServices.getPricingCurrentBand(
        { gfc: gfc.trim() },
        abortController.signal
      );

      if (response.data?.currentBand) {
        setCurrentBandData(response.data.currentBand);
      } else {
        setCurrentBandData(null);
        if (response.data?.error) {
          setCurrentBandError(response.data.error);
        }
      }
    } catch (error) {
      // Don't set error if request was aborted
      if (error instanceof Error && error.name === 'CanceledError') {
        return;
      }
      setCurrentBandError('Failed to load current price band data');
      setCurrentBandData(null);
    } finally {
      setIsCurrentBandLoading(false);
    }
  }, [gfc]);

  // ===== FETCH HISTORICAL TREND =====
  const fetchHistoricalTrendData = useCallback(async () => {
    if (!gfc || gfc.trim() === '') {
      setTrendMonthlyData([]);
      setTrendQuarterlyData([]);
      setIsHistoricalTrendLoading(false);
      return;
    }

    // Abort previous request if it exists
    if (historicalTrendAbortControllerRef.current) {
      historicalTrendAbortControllerRef.current.abort();
      historicalTrendAbortControllerRef.current = null;
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    historicalTrendAbortControllerRef.current = abortController;

    setIsHistoricalTrendLoading(true);
    setHistoricalTrendError(null);

    try {
      const response = await productWikiServices.getPricingHistoricalTrend(
        { gfc: gfc.trim() },
        abortController.signal
      );

      if (response.data) {
        setTrendMonthlyData(response.data.trendMonthly || []);
        setTrendQuarterlyData(response.data.trendQuarterly || []);
        if (response.data.error) {
          setHistoricalTrendError(response.data.error);
        }
      } else {
        setTrendMonthlyData([]);
        setTrendQuarterlyData([]);
      }
    } catch (error) {
      // Don't set error if request was aborted
      if (error instanceof Error && error.name === 'CanceledError') {
        return;
      }
      setHistoricalTrendError('Failed to load historical trend data');
      setTrendMonthlyData([]);
      setTrendQuarterlyData([]);
    } finally {
      setIsHistoricalTrendLoading(false);
    }
  }, [gfc]);

  // ===== FETCH LABELER PRICING =====
  const fetchLabelerPricingData = useCallback(async () => {
    if (!gfc || gfc.trim() === '') {
      setLabelerPricingData([]);
      setIsLabelerPricingLoading(false);
      return;
    }

    // Abort previous request if it exists
    if (labelerPricingAbortControllerRef.current) {
      labelerPricingAbortControllerRef.current.abort();
      labelerPricingAbortControllerRef.current = null;
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    labelerPricingAbortControllerRef.current = abortController;

    setIsLabelerPricingLoading(true);
    setLabelerPricingError(null);

    try {
      const response = await productWikiServices.getPricingLabeler(
        { gfc: gfc.trim() },
        abortController.signal
      );

      if (response.data?.currentPricePerLabeler) {
        setLabelerPricingData(response.data.currentPricePerLabeler);
      } else {
        setLabelerPricingData([]);
        if (response.data?.error) {
          setLabelerPricingError(response.data.error);
        }
      }
    } catch (error) {
      // Don't set error if request was aborted
      if (error instanceof Error && error.name === 'CanceledError') {
        return;
      }
      setLabelerPricingError('Failed to load labeler pricing data');
      setLabelerPricingData([]);
    } finally {
      setIsLabelerPricingLoading(false);
    }
  }, [gfc]);

  // Effect to fetch all pricing data in parallel when GFC changes
  useEffect(() => {
    fetchCurrentBandData();
    fetchHistoricalTrendData();
    fetchLabelerPricingData();

    // Cleanup: abort all requests when effect re-runs or component unmounts
    return () => {
      if (currentBandAbortControllerRef.current) {
        currentBandAbortControllerRef.current.abort();
        currentBandAbortControllerRef.current = null;
      }
      if (historicalTrendAbortControllerRef.current) {
        historicalTrendAbortControllerRef.current.abort();
        historicalTrendAbortControllerRef.current = null;
      }
      if (labelerPricingAbortControllerRef.current) {
        labelerPricingAbortControllerRef.current.abort();
        labelerPricingAbortControllerRef.current = null;
      }
    };
  }, [fetchCurrentBandData, fetchHistoricalTrendData, fetchLabelerPricingData]);

  // Fetch AI Insights for pricing
  const fetchAIInsights = useCallback(async () => {
    if (!gfc || gfc.trim() === '') {
      setAiInsights(null);
      setIsInsightsLoading(false);
      return;
    }

    // Abort previous insights request if it exists
    if (insightsAbortControllerRef.current) {
      insightsAbortControllerRef.current.abort();
      insightsAbortControllerRef.current = null;
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    insightsAbortControllerRef.current = abortController;

    setIsInsightsLoading(true);
    setInsightsError(null);

    try {
      const response = await productWikiServices.generateAIInsights(
        {
          insight_type: 'pricing',
          gfc: gfc.trim(),
          period_option: '12 months',
          llm_backend: "anthropic",
        },
        abortController.signal
      );

      if (response.data?.success && response.data?.insights) {
        setAiInsights(response.data.insights);
      } else {
        setAiInsights(null);
        if (response.data?.error || response.data?.message) {
          // Combine error and message for a complete error display
          const errorParts: string[] = [];
          if (response.data.error) errorParts.push(response.data.error);
          if (response.data.message && response.data.message !== response.data.error) {
            errorParts.push(response.data.message);
          }
          setInsightsError(errorParts.join(' - ') || 'Failed to load insights');
        }
      }
    } catch (error) {
      // Don't set error if request was aborted
      if (error instanceof Error && error.name === 'CanceledError') {
        return;
      }
      setInsightsError('Failed to load AI insights');
      setAiInsights(null);
    } finally {
      setIsInsightsLoading(false);
    }
  }, [gfc]);

  // Effect to fetch AI insights when GFC changes
  useEffect(() => {
    fetchAIInsights();

    // Cleanup: abort request when effect re-runs or component unmounts
    return () => {
      if (insightsAbortControllerRef.current) {
        insightsAbortControllerRef.current.abort();
        insightsAbortControllerRef.current = null;
      }
    };
  }, [fetchAIInsights]);

  // Fetch Price Elasticity data - separate API call for price-volume relationship
  const fetchPriceElasticityData = useCallback(async () => {
    if (!gfc || gfc.trim() === '') {
      setPriceElasticityData(null);
      setIsPriceElasticityLoading(false);
      return;
    }

    // Abort previous price elasticity request if it exists
    if (priceElasticityAbortControllerRef.current) {
      priceElasticityAbortControllerRef.current.abort();
      priceElasticityAbortControllerRef.current = null;
    }

    // Create new abort controller for this request
    const abortController = new AbortController();
    priceElasticityAbortControllerRef.current = abortController;

    setIsPriceElasticityLoading(true);
    setPriceElasticityError(null);

    try {
      const response = await productWikiServices.getPriceElasticityData(
        { gfc: gfc.trim() },
        abortController.signal
      );

      if (response.data) {
        setPriceElasticityData(response.data);
      } else {
        setPriceElasticityData(null);
        setPriceElasticityError('No price elasticity data available');
      }
    } catch (error) {
      // Don't set error if request was aborted
      if (error instanceof Error && error.name === 'CanceledError') {
        return;
      }
      setPriceElasticityError('Failed to load price elasticity data');
      setPriceElasticityData(null);
    } finally {
      setIsPriceElasticityLoading(false);
    }
  }, [gfc]);

  // Effect to fetch price elasticity data when GFC changes
  useEffect(() => {
    fetchPriceElasticityData();

    // Cleanup: abort request when effect re-runs or component unmounts
    return () => {
      if (priceElasticityAbortControllerRef.current) {
        priceElasticityAbortControllerRef.current.abort();
        priceElasticityAbortControllerRef.current = null;
      }
    };
  }, [fetchPriceElasticityData]);

  // Price engine inputs state
  const [engineInputs, setEngineInputs] = useState({
    targetMargin: 35,
    cogsEstimate: 12.5,
    discountRate: 15,
  });

  // Price engine outputs state
  const [engineOutputs, setEngineOutputs] = useState({
    suggestedWac: '$18.40-19.10',
    expectedGovNet: '$16.10',
    grossMargin: '32%',
  });

  // Update pricing engine calculations
  const updatePricingEngine = () => {
    if (!currentBandData) return;

    const { targetMargin, cogsEstimate, discountRate } = engineInputs;

    // Calculate required WAC
    const requiredWac = cogsEstimate / (1 - targetMargin / 100 - discountRate / 100);

    // Use actual market data from API
    const marketMedian = currentBandData.median;
    const marketQ1 = currentBandData.q1;

    // Calculate range
    const wacLow = Math.max(requiredWac, marketQ1 * 0.98);
    const wacHigh = Math.min(marketMedian, requiredWac * 1.05);

    // Calculate outputs
    const govNet = requiredWac * (1 - discountRate / 100);
    const grossMargin = ((requiredWac - cogsEstimate) / requiredWac) * 100;

    setEngineOutputs({
      suggestedWac: `$${wacLow.toFixed(2)}-${wacHigh.toFixed(2)}`,
      expectedGovNet: `$${govNet.toFixed(2)}`,
      grossMargin: `${grossMargin.toFixed(1)}%`,
    });
  };

  useEffect(() => {
    updatePricingEngine();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engineInputs, currentBandData]);

  // Draw price box plot on canvas
  useEffect(() => {
    if (
      priceBoxPlotRef.current &&
      currentBandData &&
      currentBandData.median !== undefined &&
      currentBandData.q1 !== undefined &&
      currentBandData.q3 !== undefined
    ) {
      const ctx = priceBoxPlotRef.current.getContext('2d');
      if (ctx) {
        drawPriceBoxPlot(ctx, currentBandData);
      }
    } else if (priceBoxPlotRef.current) {
      // Clear canvas if no data
      const ctx = priceBoxPlotRef.current.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
      }
    }
  }, [currentBandData]);

  const drawPriceBoxPlot = (ctx: CanvasRenderingContext2D, currentBand: CurrentPriceBand) => {
    const canvas = ctx.canvas;
    const width = canvas.width;
    const height = canvas.height;
    const centerY = height / 2;

    ctx.clearRect(0, 0, width, height);

    // Get price data
    const { min, q1, median, q3, max } = currentBand;

    // Calculate price range for scaling (add 10% padding on each side)
    const minPrice = min * 0.9;
    const maxPrice = max * 1.1;
    const priceRange = maxPrice - minPrice;

    // Chart dimensions
    const padding = { left: 60, right: 60, top: 40, bottom: 40 };
    const chartWidth = width - padding.left - padding.right;

    // Helper function to convert price to X coordinate
    const priceToX = (price: number) => {
      return padding.left + ((price - minPrice) / priceRange) * chartWidth;
    };

    // Calculate positions (use actual min/max for whiskers, padded values for scaling)
    const actualMinX = priceToX(min);
    const q1X = priceToX(q1);
    const medianX = priceToX(median);
    const q3X = priceToX(q3);
    const actualMaxX = priceToX(max);

    // Box height
    const boxHeight = 60;
    const boxTop = centerY - boxHeight / 2;
    const boxBottom = centerY + boxHeight / 2;

    // Brand colors for box plot
    // Using Teal as primary for box, Gold for median (to differentiate from other charts)
    const boxFillColor = 'rgba(2, 153, 153, 0.3)'; // Brand Teal with opacity
    const boxStrokeColor = '#029999'; // Brand Teal solid
    const medianColor = '#C79213'; // Brand Gold (Pantone 1245 C)
    const whiskerColor = '#522D6E'; // Brand Purple for whiskers

    // Draw whiskers (min to max line using actual min/max)
    ctx.strokeStyle = whiskerColor;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(actualMinX, centerY);
    ctx.lineTo(actualMaxX, centerY);
    ctx.stroke();

    // Draw whisker end caps
    ctx.beginPath();
    ctx.moveTo(actualMinX, centerY - 15);
    ctx.lineTo(actualMinX, centerY + 15);
    ctx.moveTo(actualMaxX, centerY - 15);
    ctx.lineTo(actualMaxX, centerY + 15);
    ctx.stroke();

    // Draw box (Q1 to Q3)
    const boxWidth = q3X - q1X;
    ctx.fillStyle = boxFillColor;
    ctx.fillRect(q1X, boxTop, boxWidth, boxHeight);
    ctx.strokeStyle = boxStrokeColor;
    ctx.lineWidth = 2;
    ctx.strokeRect(q1X, boxTop, boxWidth, boxHeight);

    // Draw median line
    ctx.strokeStyle = medianColor;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(medianX, boxTop);
    ctx.lineTo(medianX, boxBottom);
    ctx.stroke();
  };

  // ===== OFFICIAL BRAND COLOR PALETTE =====
  // Colors from official brand style guide (Pantone)
  // Primary: Pantone 269 C (#522D6E) - dominant, Pantone 1245 C (#C79213) - accent
  // Secondary: #990799 (Magenta), #029999 (Teal), #785687 (Light Purple), #E9E9F2 (Light Gray)

  // Color palette for Historical Pricing Trend (scatter plot) - original colors
  const TREND_CHART_COLORS = [
    '#3498db', // Blue
    '#2ecc71', // Green
    '#f39c12', // Orange
    '#e74c3c', // Red
    '#9b59b6', // Purple
    '#1abc9c', // Turquoise
    '#e67e22', // Dark Orange
    '#34495e', // Dark Blue
    '#16a085', // Dark Turquoise
    '#c0392b', // Dark Red
  ];

  // Color palette for Current Price per Labeler (bar chart) - brand colors starting with Teal
  const BAR_CHART_COLORS = [
    '#029999', // Brand Teal - Secondary (start different)
    '#522D6E', // Brand Purple (Pantone 269 C)
    '#C79213', // Brand Gold (Pantone 1245 C)
    '#990799', // Brand Magenta - Secondary
    '#785687', // Brand Light Purple - Secondary
    '#7B4B9F', // Secondary Purple variant
    '#4d2e6d', // Dark Purple variant
    '#64748b', // Slate gray
  ];

  // Generate color for labeler based on index (for trend chart - original colors)
  const getLabelerColor = (index: number): string => {
    return TREND_CHART_COLORS[index % TREND_CHART_COLORS.length];
  };

  // Generate color for bar chart based on index (brand colors)
  const _getBarChartColor = (index: number): string => {
    return BAR_CHART_COLORS[index % BAR_CHART_COLORS.length];
  };

  // Format labeler key to display name (convert underscores to spaces, capitalize)
  const formatLabelerName = (key: string): string => {
    return key
      .replace(/_/g, ' ')
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  // Extract all labeler keys dynamically from current trend data (exclude 'period' and 'average')
  const labelerKeys = useMemo(() => {
    if (!currentTrendData || currentTrendData.length === 0) return [];

    const keys = new Set<string>();
    currentTrendData.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'period' && key !== 'average') {
          keys.add(key);
        }
      });
    });

    return Array.from(keys).sort(); // Sort alphabetically for consistency
  }, [currentTrendData]);

  // Prepare scatter plot data for historical pricing trend
  const scatterPlotData = useMemo(() => {
    if (!currentTrendData || currentTrendData.length === 0 || labelerKeys.length === 0) return null;

    // Create scatter datasets for each labeler
    const scatterDatasets = labelerKeys.map((labelerKey, index) => {
      return {
        type: 'scatter' as const,
        label: formatLabelerName(labelerKey),
        data: currentTrendData.map((item, idx) => {
          const itemRecord = item as unknown as Record<string, number>;
          return {
            x: idx,
            y: itemRecord[labelerKey] || 0,
          };
        }),
        backgroundColor: getLabelerColor(index),
        borderColor: getLabelerColor(index),
        pointRadius: 5,
        pointHoverRadius: 7,
      };
    });

    return {
      labels: currentTrendData.map(item => item.period),
      datasets: scatterDatasets,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTrendData, labelerKeys]);

  // Helper function to check if Historical Trend chart is zoomed or panned
  const checkHistoricalTrendZoomState = useCallback(() => {
    const chartRef = historicalTrendChartRef.current;
    if (chartRef) {
      // react-chartjs-2 v5: chart instance is available through different paths
      const chartInstance = 
        (chartRef as unknown as ChartRefWrapper).chartInstance || 
        (chartRef as unknown as ChartRefWrapper).chart || 
        (chartRef as unknown as ChartRefWrapper).getChart?.() ||
        chartRef;
      
      if (chartInstance && typeof chartInstance.isZoomedOrPanned === 'function') {
        try {
          const isZoomed = chartInstance.isZoomedOrPanned();
          setIsHistoricalTrendZoomed(isZoomed);
          return isZoomed;
        } catch (error) {
          console.warn('Error checking Historical Trend zoom state:', error);
        }
      }
    }
    return false;
  }, []);

  // Helper function to check if Historical Trend modal chart is zoomed or panned
  const checkHistoricalTrendModalZoomState = useCallback(() => {
    const chartRef = historicalTrendModalChartRef.current;
    if (chartRef) {
      // react-chartjs-2 v5: chart instance is available through different paths
      const chartInstance = 
        (chartRef as unknown as ChartRefWrapper).chartInstance || 
        (chartRef as unknown as ChartRefWrapper).chart || 
        (chartRef as unknown as ChartRefWrapper).getChart?.() ||
        chartRef;
      
      if (chartInstance && typeof chartInstance.isZoomedOrPanned === 'function') {
        try {
          const isZoomed = chartInstance.isZoomedOrPanned();
          setIsHistoricalTrendModalZoomed(isZoomed);
          return isZoomed;
        } catch (error) {
          console.warn('Error checking Historical Trend modal zoom state:', error);
        }
      }
    }
    return false;
  }, []);

  const scatterPlotOptions: ChartOptions<'scatter' | 'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'right' as const,
          labels: {
            usePointStyle: true,
            padding: 15,
            generateLabels: function (chart) {
              const datasets = chart.data.datasets || [];
              const totalDatasets = datasets.length;
              
              // Generate legend items from datasets
              type DatasetWithStyles = { backgroundColor?: string; borderColor?: string; borderWidth?: number };
              const legendItems = datasets.map((dataset, index) => {
                const styledDataset = dataset as DatasetWithStyles;
                return {
                  text: dataset.label || `Dataset ${index + 1}`,
                  fillStyle: styledDataset.backgroundColor || styledDataset.borderColor || '#000',
                  strokeStyle: styledDataset.borderColor || styledDataset.backgroundColor || '#000',
                  lineWidth: styledDataset.borderWidth || 0,
                  hidden: dataset.hidden || false,
                  index: index,
                  datasetIndex: index,
                };
              });
              
              if (totalDatasets <= 10) {
                // If 10 or fewer datasets, show all
                return legendItems;
              }
              
              // Show first 10 labels
              const limitedLabels = legendItems.slice(0, 10);
              
              // Add "Total - X more" entry
              const remainingCount = totalDatasets - 10;
              limitedLabels.push({
                text: `...and ${remainingCount} more`,
                fillStyle: 'transparent',
                strokeStyle: 'transparent',
                lineWidth: 0,
                hidden: false,
                index: 10,
                datasetIndex: -1, // Not associated with any dataset
              });
              
              return limitedLabels;
            },
          },
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const period = currentTrendData[context.dataIndex]?.period || '';
              const yValue = context.parsed.y ?? 0;
              return `${context.dataset.label}: $${yValue.toFixed(2)} (${period})`;
            },
          },
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'y' as const, // Y-axis only zoom
            scaleMode: 'y' as const, // Y-axis only scale mode
          },
          pan: {
            enabled: true,
            mode: 'y' as const, // Y-axis only pan
          },
          limits: {
            x: {
              min: 'original' as const,
              max: 'original' as const,
            },
            y: {
              min: 'original' as const,
              max: 'original' as const,
            },
          },
          onZoom: (_context: { chart: ChartJS }) => {
            // Set zoomed state immediately when zoom occurs
            setIsHistoricalTrendZoomed(true);
          },
          onZoomComplete: (context: { chart: ChartJS }) => {
            // Verify zoom state after zoom completes
            if (context?.chart) {
              const chartInstance = context.chart;
              if (typeof chartInstance.isZoomedOrPanned === 'function') {
                const isZoomed = chartInstance.isZoomedOrPanned();
                setIsHistoricalTrendZoomed(isZoomed);
              } else {
                setIsHistoricalTrendZoomed(true);
              }
            } else {
              // Fallback: check using ref
              setTimeout(() => {
                checkHistoricalTrendZoomState();
              }, 100);
            }
          },
          onPan: (_context: { chart: ChartJS }) => {
            // Also show reset button when panning
            setIsHistoricalTrendZoomed(true);
          },
          onPanComplete: (context: { chart: ChartJS }) => {
            // Check zoom/pan state after pan completes
            if (context?.chart) {
              const chartInstance = context.chart;
              if (typeof chartInstance.isZoomedOrPanned === 'function') {
                const isZoomed = chartInstance.isZoomedOrPanned();
                setIsHistoricalTrendZoomed(isZoomed);
              } else {
                setIsHistoricalTrendZoomed(true);
              }
            } else {
              // Fallback: check using ref
              setTimeout(() => {
                checkHistoricalTrendZoomState();
              }, 100);
            }
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: trendGranularity === 'monthly' ? 'Month' : 'Quarter',
            font: {
              size: 15,
            },
          },
          ticks: {
            callback: function (value) {
              return currentTrendData[value as number]?.period || '';
            },
            maxRotation: 45,
            minRotation: 45,
            font: {
              size: 15,
            },
          },
        },
        y: {
          title: {
            display: true,
            text: 'Price per Unit ($)',
            font: {
              size: 15,
            },
          },
          beginAtZero: false,
          ticks: {
            font: {
              size: 15,
            },
          },
        },
      },
    }),
    [currentTrendData, trendGranularity, checkHistoricalTrendZoomState]
  );

  // Chart options for modal (larger view)
  const scatterPlotModalOptions: ChartOptions<'scatter' | 'line'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'right' as const,
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 14,
            },
          },
        },
        title: {
          display: false, // Hide title in modal since it's in the header
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const period = currentTrendData[context.dataIndex]?.period || '';
              const yValue = context.parsed.y ?? 0;
              return `${context.dataset.label}: $${yValue.toFixed(2)} (${period})`;
            },
          },
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'y' as const, // Y-axis only zoom
            scaleMode: 'y' as const, // Y-axis only scale mode
          },
          pan: {
            enabled: true,
            mode: 'y' as const, // Y-axis only pan
          },
          limits: {
            x: {
              min: 'original' as const,
              max: 'original' as const,
            },
            y: {
              min: 'original' as const,
              max: 'original' as const,
            },
          },
          onZoom: (_context: { chart: ChartJS }) => {
            // Set zoomed state immediately when zoom occurs
            setIsHistoricalTrendModalZoomed(true);
          },
          onZoomComplete: (context: { chart: ChartJS }) => {
            // Verify zoom state after zoom completes
            if (context?.chart) {
              const chartInstance = context.chart;
              if (typeof chartInstance.isZoomedOrPanned === 'function') {
                const isZoomed = chartInstance.isZoomedOrPanned();
                setIsHistoricalTrendModalZoomed(isZoomed);
              } else {
                setIsHistoricalTrendModalZoomed(true);
              }
            } else {
              // Fallback: check using ref
              setTimeout(() => {
                checkHistoricalTrendModalZoomState();
              }, 100);
            }
          },
          onPan: (_context: { chart: ChartJS }) => {
            // Also show reset button when panning
            setIsHistoricalTrendModalZoomed(true);
          },
          onPanComplete: (context: { chart: ChartJS }) => {
            // Check zoom/pan state after pan completes
            if (context?.chart) {
              const chartInstance = context.chart;
              if (typeof chartInstance.isZoomedOrPanned === 'function') {
                const isZoomed = chartInstance.isZoomedOrPanned();
                setIsHistoricalTrendModalZoomed(isZoomed);
              } else {
                setIsHistoricalTrendModalZoomed(true);
              }
            } else {
              // Fallback: check using ref
              setTimeout(() => {
                checkHistoricalTrendModalZoomState();
              }, 100);
            }
          },
        },
      },
      scales: {
        x: {
          title: {
            display: true,
            text: trendGranularity === 'monthly' ? 'Month' : 'Quarter',
            font: {
              size: 16,
              weight: 'bold',
            },
          },
          ticks: {
            callback: function (value) {
              return currentTrendData[value as number]?.period || '';
            },
            maxRotation: 45,
            minRotation: 45,
            font: {
              size: 13,
            },
          },
        },
        y: {
          title: {
            display: true,
            text: 'Price per Unit ($)',
            font: {
              size: 16,
              weight: 'bold',
            },
          },
          beginAtZero: false,
          ticks: {
            font: {
              size: 13,
            },
          },
        },
      },
    }),
    [currentTrendData, trendGranularity, checkHistoricalTrendModalZoomState]
  );

  // Prepare bar chart data for Current Price per Labeler (VA/DOD sources)
  // Uses same colors as Market Structure plot for consistency
  const currentPriceBarData = useMemo(() => {
    if (!labelerPricingData || labelerPricingData.length === 0) {
      return null;
    }

    const backgroundColor = '#522D6E';
    const borderColor = '#522D6E';

    return {
      labels: labelerPricingData.map(item => item.labeler),
      datasets: [
        {
          label: 'Current Price ($)',
          data: labelerPricingData.map(item => item.price),
          backgroundColor: backgroundColor,
          borderColor: borderColor,
          borderWidth: 2,
          borderRadius: 4,
          hoverBackgroundColor: borderColor,
          hoverBorderColor: borderColor,
        },
      ],
    };
  }, [labelerPricingData]);

  // Bar chart options for Current Price per Labeler
  const currentPriceBarOptions: ChartOptions<'bar'> = useMemo(
    () => ({
      indexAxis: 'x' as const, // Vertical bars - labelers on X-axis, prices on Y-axis
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false, // Hide legend since we have a title outside the chart
        },
        title: {
          display: false, // Title is shown as external header
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleColor: '#374151',
          bodyColor: '#374151',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          padding: 12,
          callbacks: {
            label: function (context) {
              const value = context.parsed.y ?? 0;
              return `Price: $${value.toFixed(2)}`;
            },
          },
        },
      },
      scales: {
        x: {
          type: 'category' as const,
          title: {
            display: true,
            text: 'Labeler',
            color: '#374151',
            font: {
              size: 12,
              weight: 'bold' as const,
            },
          },
          ticks: {
            color: '#374151',
            font: {
              size: 15,
            },
            maxRotation: 45,
            minRotation: 0,
            autoSkip: false,
            callback: function (value, _index, _ticks) {
              // Get the label from the tick
              const label = this.getLabelForValue(value as number);
              // Truncate to first 5 characters
              if (typeof label === 'string' && label.length > 5) {
                return label.substring(0, 5) + '...';
              }
              return label;
            },
          },
          grid: {
            display: false,
          },
        },
        y: {
          type: 'linear' as const,
          title: {
            display: true,
            text: 'Price per Unit ($)',
            color: '#374151',
            font: {
              size: 12,
              weight: 'bold' as const,
            },
          },
          beginAtZero: true, // Always start y-axis from 0
          ticks: {
            color: '#374151',
            callback: function (value) {
              return `$${value}`;
            },
            font: {
              size: 15,
            },
          },
          grid: {
            color: '#e5e7eb',
          },
        },
      },
    }),
    []
  );

  // Prepare scatter plot data for Price Elasticity chart
  const priceElasticityScatterData = useMemo(() => {
    if (
      !priceElasticityData ||
      !priceElasticityData.pricePoints ||
      !priceElasticityData.volumePoints ||
      priceElasticityData.pricePoints.length === 0
    ) {
      return null;
    }

    const { pricePoints, volumePoints, trendLine, currentPrice, currentVolume } =
      priceElasticityData;

    interface ChartDatasetItem {
      type: 'scatter' | 'line';
      label: string;
      data: { x: number; y: number }[];
      backgroundColor: string;
      borderColor: string;
      borderWidth: number;
      pointRadius: number;
      pointHoverRadius?: number;
      pointStyle?: string;
      borderDash?: number[];
      fill?: boolean;
      tension?: number;
    }
    const datasets: ChartDatasetItem[] = [];

    // Brand colors for Price Elasticity chart
    // Using Magenta for data points (different from other charts), Purple for current market
    const dataPointColor = '#522D6E'; // Brand Purple
    const currentMarketColor = '#522D6E'; // Brand Purple (Pantone 269 C)
    const trendLineColor = '#C79213'; // Brand Gold (Pantone 1245 C)

    // Data points scatter dataset
    const dataPointsDataset = {
      type: 'scatter' as const,
      label: 'Price vs Volume',
      data: pricePoints.map((price, index) => ({
        x: price,
        y: volumePoints[index],
      })),
      backgroundColor: dataPointColor,
      borderColor: dataPointColor,
      borderWidth: 2,
      pointRadius: 6,
      pointHoverRadius: 8,
    };
    datasets.push(dataPointsDataset);

    // Current market point (if available)
    if (currentPrice !== null && currentVolume !== null) {
      const currentMarketDataset = {
        type: 'scatter' as const,
        label: 'Current Market',
        data: [{ x: currentPrice, y: currentVolume }],
        backgroundColor: currentMarketColor,
        borderColor: currentMarketColor,
        borderWidth: 2,
        pointRadius: 10,
        pointHoverRadius: 12,
        pointStyle: 'circle',
      };
      datasets.push(currentMarketDataset);
    }

    // Trend line dataset (as line type)
    if (trendLine && trendLine.points && trendLine.points.length === 2) {
      const trendLineDataset = {
        type: 'line' as const,
        label: 'Trend Line',
        data: trendLine.points.map(point => ({ x: point.x, y: point.y })),
        backgroundColor: 'transparent',
        borderColor: trendLineColor, // Brand Gold
        borderWidth: 2,
        borderDash: [5, 5],
        pointRadius: 0,
        fill: false,
        tension: 0,
      };
      datasets.push(trendLineDataset);
    }

    return { datasets };
  }, [priceElasticityData]);

  // Helper function to format large volume numbers for display
  const formatVolumeValue = (value: number): string => {
    if (value >= 1000000 || value <= -1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000 || value <= -1000) {
      return `${(value / 1000).toFixed(1)}K`;
    }
    return value.toFixed(0);
  };

  // Helper function to format volume for tooltip (more detailed)
  const formatVolumeTooltip = (value: number): string => {
    if (value >= 1000000 || value <= -1000000) {
      return `${(value / 1000000).toFixed(2)}M (${value.toLocaleString()})`;
    } else if (value >= 1000 || value <= -1000) {
      return `${(value / 1000).toFixed(2)}K (${value.toLocaleString()})`;
    }
    return value.toLocaleString();
  };

  // Helper function to check if chart is zoomed or panned
  const checkZoomState = useCallback(() => {
    const chartRef = priceElasticityChartRef.current;
    if (chartRef) {
      // react-chartjs-2 v5: chart instance is available through different paths
      const chartInstance = 
        (chartRef as unknown as ChartRefWrapper).chartInstance || 
        (chartRef as unknown as ChartRefWrapper).chart || 
        (chartRef as unknown as ChartRefWrapper).getChart?.() ||
        chartRef;
      
      if (chartInstance && typeof chartInstance.isZoomedOrPanned === 'function') {
        try {
          const isZoomed = chartInstance.isZoomedOrPanned();
          setIsPriceElasticityZoomed(isZoomed);
          return isZoomed;
        } catch (error) {
          console.warn('Error checking zoom state:', error);
        }
      }
    }
    return false;
  }, []);

  // Scatter plot options for Price Elasticity chart
  const priceElasticityScatterOptions: ChartOptions<'scatter'> = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false, // Hide legend as requested
        },
        title: {
          display: false,
        },
        tooltip: {
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleColor: '#374151',
          bodyColor: '#374151',
          borderColor: '#e5e7eb',
          borderWidth: 1,
          padding: 12,
          callbacks: {
            label: function (context) {
              const xValue = context.parsed.x ?? 0;
              const yValue = context.parsed.y ?? 0;
              return `Price: $${xValue.toFixed(2)}, Volume: ${formatVolumeTooltip(yValue)}`;
            },
          },
        },
        zoom: {
          zoom: {
            wheel: {
              enabled: true,
            },
            pinch: {
              enabled: true,
            },
            mode: 'xy' as const,
            scaleMode: 'xy' as const,
          },
          pan: {
            enabled: true,
            mode: 'xy' as const,
          },
          limits: {
            x: {
              min: 'original' as const,
              max: 'original' as const,
            },
            y: {
              min: 0,
              max: 'original' as const,
            },
          },
          onZoom: (_context: { chart: ChartJS }) => {
            // Set zoomed state immediately when zoom occurs
            setIsPriceElasticityZoomed(true);
          },
          onZoomComplete: (context: { chart: ChartJS }) => {
            // Verify zoom state after zoom completes
            if (context?.chart) {
              const chartInstance = context.chart;
              if (typeof chartInstance.isZoomedOrPanned === 'function') {
                const isZoomed = chartInstance.isZoomedOrPanned();
                setIsPriceElasticityZoomed(isZoomed);
              } else {
                setIsPriceElasticityZoomed(true);
              }
            } else {
              // Fallback: check using ref
              setTimeout(() => {
                checkZoomState();
              }, 100);
            }
          },
          onPan: (_context: { chart: ChartJS }) => {
            // Also show reset button when panning
            setIsPriceElasticityZoomed(true);
          },
          onPanComplete: (context: { chart: ChartJS }) => {
            // Check zoom/pan state after pan completes
            if (context?.chart) {
              const chartInstance = context.chart;
              if (typeof chartInstance.isZoomedOrPanned === 'function') {
                const isZoomed = chartInstance.isZoomedOrPanned();
                setIsPriceElasticityZoomed(isZoomed);
              } else {
                setIsPriceElasticityZoomed(true);
              }
            } else {
              // Fallback: check using ref
              setTimeout(() => {
                checkZoomState();
              }, 100);
            }
          },
        },
      },
      scales: {
        x: {
          type: 'linear' as const,
          title: {
            display: true,
            text: 'Price per Unit ($)',
            color: '#374151',
            font: {
              size: 12,
              weight: 'bold' as const,
            },
          },
          ticks: {
            color: '#374151',
            callback: function (value) {
              return `$${Number(value).toFixed(2)}`;
            },
            font:{
              size: 15,
            }
          },
          grid: {
            color: '#e5e7eb',
          },
        },
        y: {
          type: 'linear' as const,
          title: {
            display: true,
            text: 'Volume (Units)',
            color: '#374151',
            font: {
              size: 12,
              weight: 'bold' as const,
            },
          },
          beginAtZero: true,
          min: 0,
          ticks: {
            color: '#374151',
            callback: function (value) {
              return formatVolumeValue(Number(value));
            },
            maxTicksLimit: 8, // Limit number of ticks for cleaner display
            font: {
              size: 15,
            },
          },
          grid: {
            color: '#e5e7eb',
          },
        },
      },
    }),
    [checkZoomState]
  );

  // Reset zoom function for Price Elasticity chart
  // Uses chartjs-plugin-zoom API: chart.resetZoom(mode = 'none'): void
  // Resets the current chart bounds to the defaults that were used before any zooming or panning occurred
  const resetPriceElasticityZoom = useCallback(() => {
    const chartRef = priceElasticityChartRef.current;
    if (chartRef) {
      // react-chartjs-2: access chart instance through the ref
      // The chart instance is available directly or through chartInstance property
      const chartInstance = (chartRef as unknown as ChartRefWrapper).chartInstance || (chartRef as unknown as ChartRefWrapper).chart || chartRef;
      
      if (chartInstance && typeof chartInstance.resetZoom === 'function') {
        // Call resetZoom() to reset chart bounds to defaults before zooming/panning
        // Using default mode 'none' for immediate reset without animation
        chartInstance.resetZoom();
        
        // Update zoom state - check if still zoomed after reset
        // Use isZoomedOrPanned() API method if available
        if (typeof chartInstance.isZoomedOrPanned === 'function') {
          setIsPriceElasticityZoomed(chartInstance.isZoomedOrPanned());
        } else {
          // Fallback: assume reset was successful
          setIsPriceElasticityZoomed(false);
        }
      }
    }
  }, []);

  // Reset zoom function for Historical Pricing Trend chart
  // Uses chartjs-plugin-zoom API: chart.resetZoom(mode = 'none'): void
  // Resets the current chart bounds to the defaults that were used before any zooming or panning occurred
  const resetHistoricalTrendZoom = useCallback(() => {
    const chartRef = historicalTrendChartRef.current;
    if (chartRef) {
      // react-chartjs-2: access chart instance through the ref
      // The chart instance is available directly or through chartInstance property
      const chartInstance = (chartRef as unknown as ChartRefWrapper).chartInstance || (chartRef as unknown as ChartRefWrapper).chart || chartRef;
      
      if (chartInstance && typeof chartInstance.resetZoom === 'function') {
        // Call resetZoom() to reset chart bounds to defaults before zooming/panning
        // Using default mode 'none' for immediate reset without animation
        chartInstance.resetZoom();
        
        // Update zoom state - check if still zoomed after reset
        // Use isZoomedOrPanned() API method if available
        if (typeof chartInstance.isZoomedOrPanned === 'function') {
          setIsHistoricalTrendZoomed(chartInstance.isZoomedOrPanned());
        } else {
          // Fallback: assume reset was successful
          setIsHistoricalTrendZoomed(false);
        }
      }
    }
  }, []);

  // Reset zoom function for Historical Pricing Trend modal chart
  // Uses chartjs-plugin-zoom API: chart.resetZoom(mode = 'none'): void
  // Resets the current chart bounds to the defaults that were used before any zooming or panning occurred
  const resetHistoricalTrendModalZoom = useCallback(() => {
    const chartRef = historicalTrendModalChartRef.current;
    if (chartRef) {
      // react-chartjs-2: access chart instance through the ref
      // The chart instance is available directly or through chartInstance property
      const chartInstance = (chartRef as unknown as ChartRefWrapper).chartInstance || (chartRef as unknown as ChartRefWrapper).chart || chartRef;
      
      if (chartInstance && typeof chartInstance.resetZoom === 'function') {
        // Call resetZoom() to reset chart bounds to defaults before zooming/panning
        // Using default mode 'none' for immediate reset without animation
        chartInstance.resetZoom();
        
        // Update zoom state - check if still zoomed after reset
        // Use isZoomedOrPanned() API method if available
        if (typeof chartInstance.isZoomedOrPanned === 'function') {
          setIsHistoricalTrendModalZoomed(chartInstance.isZoomedOrPanned());
        } else {
          // Fallback: assume reset was successful
          setIsHistoricalTrendModalZoomed(false);
        }
      }
    }
  }, []);

  return {
    priceView,
    setPriceView,
    isChartModalOpen,
    setIsChartModalOpen,
    engineInputs,
    setEngineInputs,
    engineOutputs,
    priceBoxPlotRef,
    scatterPlotData,
    scatterPlotOptions,
    scatterPlotModalOptions,
    labelerKeys,
    getLabelerColor,
    formatLabelerName,
    updatePricingEngine,
    // Trend granularity (monthly/quarterly) - no refetch needed!
    trendGranularity,
    setTrendGranularity,
    // Current trend data based on selected granularity
    currentTrendData,
    // ===== Current Price Band (separate loader) =====
    currentBandData,
    isCurrentBandLoading,
    currentBandError,
    refetchCurrentBand: fetchCurrentBandData,
    // ===== Historical Trend (separate loader) =====
    trendMonthlyData,
    trendQuarterlyData,
    isHistoricalTrendLoading,
    historicalTrendError,
    refetchHistoricalTrend: fetchHistoricalTrendData,
    // ===== Labeler Pricing (separate loader) =====
    labelerPricingData,
    isLabelerPricingLoading,
    labelerPricingError,
    refetchLabelerPricing: fetchLabelerPricingData,
    // Current Price per Labeler bar chart data
    currentPriceBarData,
    currentPriceBarOptions,
    // AI Insights
    aiInsights,
    isInsightsLoading,
    insightsError,
    refetchInsights: fetchAIInsights,
    // Price Elasticity data (separate loader)
    priceElasticityData,
    isPriceElasticityLoading,
    priceElasticityError,
    refetchPriceElasticity: fetchPriceElasticityData,
    // Price Elasticity scatter plot data and options
    priceElasticityScatterData,
    priceElasticityScatterOptions,
    // Zoom state and controls
    isPriceElasticityZoomed,
    priceElasticityChartRef,
    resetPriceElasticityZoom,
    // Historical Trend zoom state and controls
    isHistoricalTrendZoomed,
    historicalTrendChartRef,
    resetHistoricalTrendZoom,
    // Historical Trend modal zoom state and controls
    isHistoricalTrendModalZoomed,
    historicalTrendModalChartRef,
    resetHistoricalTrendModalZoom,
  };
};
export default usePriceTab;
